'use client';
import { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line } from 'react-chartjs-2';
import maps from 'src/styles/maps.json';
import { addPercentage } from '@get-mosh/msh-shared';
import { formatWeight } from '@get-mosh/msh-shared';
import { isMosh, isMoshy } from 'src/env.mjs';
import classNames from 'classnames';

const { colors } = maps;

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
);

type WeightLossGraphProps = {
    startWeight: number;
    halfwayMonth?: number;
    halfwayWeightPercentage?: number;
    endMonth?: number;
    endWeightPercentage?: number;
    backgroundColor?: string;
};

const WeightLossGraph = ({
    startWeight,
    halfwayMonth = 4,
    halfwayWeightPercentage = 5,
    endMonth = 12,
    endWeightPercentage = 10,
    backgroundColor,
}: WeightLossGraphProps) => {
    const options = useMemo<ChartOptions<'line'>>(() => {
        const min = addPercentage(startWeight, -15);
        const max = addPercentage(startWeight, 5);

        return {
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 32,
                },
            },
            backgroundColor: colors['white'],
            scales: {
                x: {
                    grid: {
                        display: false,
                    },
                    border: {
                        display: false,
                    },
                    offset: true,
                    ticks: {
                        font: {
                            size: 16,
                            family: 'MaisonNeue-Demi',
                        },
                        color: isMosh
                            ? colors['grey-400']
                            : colors['blue-extra-dark'],
                        maxRotation: 0,
                    },
                },
                y: {
                    min,
                    max,
                    grid: {
                        display: true,
                        lineWidth: 2,
                    },
                    border: {
                        display: false,
                    },
                    ticks: {
                        display: false,
                        stepSize: (max - min) / 5,
                    },
                },
            },
            plugins: {
                datalabels: {
                    display: (ctx) => ctx.dataIndex % 2 === 0,
                    formatter: (value) => `${value}kg`,
                    backgroundColor: isMosh
                        ? colors['green']
                        : colors['blue-extra-dark'],
                    borderRadius: 4,
                    padding: {
                        top: 4,
                        bottom: 4,
                        left: 8,
                        right: 8,
                    },
                    labels: {
                        title: {
                            font: {
                                weight: 'bold',
                                size: 16,
                            },
                            color: isMosh
                                ? colors['green-dark']
                                : colors['white'],
                        },
                    },
                    align: 'top',
                    offset: 12,
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
        };
    }, [startWeight]);

    const data = useMemo<ChartData<'line'>>(() => {
        const labels = [
            'Start',
            '',
            `${halfwayMonth} months`,
            '',
            `${endMonth} months`,
        ];

        return {
            labels,
            datasets: [
                {
                    label: 'Weight prediction',
                    data: [
                        startWeight,
                        // Extra points to make the line curve nicely
                        addPercentage(
                            startWeight,
                            -halfwayWeightPercentage + 1,
                        ),
                        addPercentage(startWeight, -halfwayWeightPercentage),
                        addPercentage(
                            startWeight,
                            -halfwayWeightPercentage - 1,
                        ),
                        addPercentage(startWeight, -endWeightPercentage),
                    ],
                    borderColor: isMosh
                        ? colors['green']
                        : colors['blue-extra-dark'],
                    backgroundColor: isMosh
                        ? colors['green']
                        : colors['blue-extra-dark'],
                    pointRadius: 8,
                    pointStyle: (ctx) =>
                        ctx.dataIndex % 2 === 0 ? 'circle' : false,
                    // Curve the line
                    tension: 0.3,
                },
            ],
        };
    }, [
        startWeight,
        halfwayMonth,
        halfwayWeightPercentage,
        endMonth,
        endWeightPercentage,
    ]);

    return data ? (
        <div
            className={classNames(
                backgroundColor,
                'flex flex-col gap-1 rounded-2xl p-4',
                {
                    'bg-white': isMosh,
                    'bg-blue-extra-light': isMoshy,
                },
            )}
        >
            <p
                className={classNames({
                    'text-green-faint-dark': isMosh,
                    'text-grey-400': isMoshy,
                })}
            >
                <strong>We predict that you could lose</strong>
            </p>
            <h4>
                {formatWeight(startWeight - addPercentage(startWeight, -10))}
                &nbsp;in {endMonth} months
            </h4>
            <div className="min-h-44">
                <Line options={options} data={data} />
            </div>
        </div>
    ) : null;
};

export default WeightLossGraph;
